import { useMemo } from 'react';
import { Group, Tooltip, ThemeIcon } from '@mantine/core';
import { BsChevronRight, BsDash, BsXCircle } from 'react-icons/bs';
import { IoMdRefresh } from 'react-icons/io';
import { OutputValueDisplay } from '../output-value';
import { FooterContainer, FooterPreview } from './styles';
import { useGraphContext } from '../../context';
import { TERMINAL_NODE_ID } from '../../config';
import { match } from 'ts-pattern';
import { NodeEvaluationStatus } from '../../types';
import { IoMdCheckmark } from 'react-icons/io';
import { t } from 'i18next';
import { DrawerKind } from '../../store/ui';

export const Footer = () => {
  const [status, value, openDrawer] = useGraphContext(({ eval: ev, ui }) => [
    ev.getNodeStatus(TERMINAL_NODE_ID),
    ev.getNodeValue(TERMINAL_NODE_ID),
    ui.openDrawer,
  ]);

  const footerStatus = useMemo(() => {
    return match(status)
      .with(NodeEvaluationStatus.Ok, () => ({
        color: 'green',
        icon: IoMdCheckmark,
        label: t('graphEditor.footer.status.success'),
      }))
      .with(NodeEvaluationStatus.Idle, () => ({
        color: 'gray',
        icon: BsDash,
        label: t('graphEditor.footer.status.idle'),
      }))
      .with(NodeEvaluationStatus.Running, () => ({
        color: 'blue',
        icon: IoMdRefresh,
        label: t('graphEditor.footer.status.running'),
      }))
      .with(NodeEvaluationStatus.Error, () => ({
        color: 'red',
        icon: BsXCircle,
        label: t('graphEditor.footer.status.error'),
      }))
      .exhaustive();
  }, [status]);

  const footerPreview = useMemo(() => value, [value]);

  return (
    <FooterContainer>
      <Group spacing="xs" align="center">
        <Tooltip label={footerStatus.label} withArrow>
          <ThemeIcon
            color={footerStatus.color}
            variant="outline"
            size="sm"
            sx={{ border: 'none' }}
          >
            <footerStatus.icon size={14} />
          </ThemeIcon>
        </Tooltip>
        <Tooltip label={t('graphEditor.common.openFullOutput')} withArrow>
          <FooterPreview
            onClick={() =>
              openDrawer({ kind: DrawerKind.GraphConfig, tab: 'output' })
            }
          >
            <OutputValueDisplay
              value={footerPreview}
              variant="inline"
              maxLength={80}
              emptyLabel={t('graphEditor.common.outputWillAppear')}
              textSx={() => ({ flex: 1 })}
            />
            <BsChevronRight size={12} />
          </FooterPreview>
        </Tooltip>
      </Group>
    </FooterContainer>
  );
};
